// src/services/auth.js
// Servicio de autenticación real (conecta con backend)

import { readApiResponse, requestJson } from './http';
import { resetActivity } from './activity';

const API_URL = import.meta.env.VITE_API_URL || '/api';

// Helper para manejar respuestas
async function handleResponse(response) {
  return readApiResponse(response);
}

// Helper para obtener token actual
function getToken() {
  return localStorage.getItem('auth_token');
}

// Helper para guardar token
function saveToken(token) {
  localStorage.setItem('auth_token', token);
}

// Helper para limpiar token
function clearToken() {
  localStorage.removeItem('auth_token');
}

// Helper para decodificar JWT (sin verificar)
function decodeToken(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decodificando token:', error);
    return null;
  }
}

class AuthService {
  constructor() {
    this.currentUser = null;
    this.loadCurrentUser();
  }

  // Cargar usuario desde token guardado
  loadCurrentUser() {
    const token = getToken();
    if (token) {
      const decoded = decodeToken(token);
      if (decoded) {
        // Verificar si el token no ha expirado
        const now = Date.now() / 1000;
        if (decoded.exp && decoded.exp > now) {
          this.currentUser = {
            id: decoded.userId,
            name: decoded.name,
            email: decoded.email,
            isDemo: decoded.isDemo || false,
            emailVerified: decoded.emailVerified || false,
            isTrial: decoded.isTrial || false,
            mustChangePassword: decoded.mustChangePassword || false
          };
        } else {
          // Token expirado, intentar renovar automáticamente
          this.refreshToken().catch(() => {
            clearToken();
            this.currentUser = null;
          });
        }
      }
    }
  }

  // REGISTRO
  async register({ email, password, name, inviteCode }) {
    try {
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name, inviteCode })
      });

      const data = await handleResponse(response);
      
      if (data.token) {
        saveToken(data.token);
        this.currentUser = data.user;
      }
      
      return data;
    } catch (error) {
      console.error('Error en registro:', error);
      throw error;
    }
  }

  async getTrialStatus() {
    const response = await fetch(`${API_URL}/auth/trial-status`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return handleResponse(response);
  }

  async activateTrial(password) {
    const response = await fetch(`${API_URL}/auth/trial-activate`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
      body: JSON.stringify({ password })
    });
    const data = await handleResponse(response);
    saveToken(data.token);
    this.currentUser = data.user;
    return data;
  }

  // LOGIN
  async login({ email, password }) {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await handleResponse(response);
      
      // Guardar token
      saveToken(data.token);
      
      // Actualizar usuario actual
      this.currentUser = data.user;
      
      return data;
    } catch (error) {
      console.error('Error en login:', error);
      throw error;
    }
  }

  // LOGOUT
  logout() {
    resetActivity();
    clearToken();
    this.currentUser = null;
    // 🧹 Limpiar TODO el localStorage relacionado con la sesión
    // Importamos dinámicamente para evitar dependencias circulares
    import('../utils/clearData.js').then(({ clearAllSessionData }) => {
      clearAllSessionData();
    }).catch(err => {
      // Fallback: limpiar manualmente si falla la importación dinámica
      console.warn('⚠️ Fallback limpieza manual:', err);
      localStorage.removeItem('profileId');
      localStorage.removeItem('numerologiaUserData');
      localStorage.removeItem('numerologiaProfiles');
      localStorage.removeItem('numerologiaCurrentProfile');
    });
  }

  // VERIFICAR EMAIL
  async verifyEmail(token) {
    try {
      const response = await fetch(`${API_URL}/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });

      const data = await handleResponse(response);
      
      // Actualizar token si viene nuevo
      if (data.token) {
        saveToken(data.token);
        this.currentUser = data.user;
      }
      
      return data;
    } catch (error) {
      console.error('Error verificando email:', error);
      throw error;
    }
  }

  // REENVIAR EMAIL DE VERIFICACIÓN
  async resendVerificationEmail() {
    // TODO: Implementar endpoint de reenvío en backend
    console.log('⚠️ Reenvío de email aún no implementado');
    return { success: false, error: 'Función no implementada aún' };
  }

  // SOLICITAR RESET DE PASSWORD
  async forgotPassword(email) {
    try {
      const response = await fetch(`${API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Error en forgot password:', error);
      throw error;
    }
  }

  // RESET DE PASSWORD
  async resetPassword({ token, newPassword }) {
    try {
      const response = await fetch(`${API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword })
      });

      const data = await handleResponse(response);
      
      // Guardar nuevo token
      if (data.token) {
        saveToken(data.token);
        this.currentUser = data.user;
      }
      
      return data;
    } catch (error) {
      console.error('Error en reset password:', error);
      throw error;
    }
  }

  // CREAR SESIÓN DEMO
  async createDemoSession(name = 'Usuario Demo') {
    try {
      const response = await fetch(`${API_URL}/auth/create-demo-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      });

      const data = await handleResponse(response);
      
      // Guardar token demo
      saveToken(data.token);
      
      // Actualizar usuario actual
      this.currentUser = data.user;
      
      return data;
    } catch (error) {
      console.error('Error creando sesión demo:', error);
      throw error;
    }
  }

  // RENOVAR TOKEN
  async refreshToken() {
    try {
      const token = getToken();
      if (!token) {
        throw new Error('No hay token para renovar');
      }

      const response = await fetch(`${API_URL}/auth/refresh-token`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await handleResponse(response);
      
      // Guardar nuevo token
      saveToken(data.token);
      
      // Actualizar usuario si viene
      if (data.user) {
        this.currentUser = data.user;
      }
      
      return data;
    } catch (error) {
      console.error('Error renovando token:', error);
      throw error;
    }
  }

  // GETTERS
  getCurrentUser() {
    return this.currentUser;
  }

  isAuthenticated() {
    return this.currentUser !== null;
  }

  isDemoMode() {
    return this.currentUser?.isDemo || false;
  }

  getToken() {
    return getToken();
  }

  needsEmailVerification() {
    return this.currentUser && !this.currentUser.isDemo && !this.currentUser.emailVerified;
  }

  // GUARDAR PERFIL COMPLETO
  async saveProfile(profileData) {
    try {
      const token = getToken();
      if (!token) {
        throw new Error('No hay sesión activa');
      }

      const response = await fetch(`${API_URL}/profiles/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(profileData)
      });

      const data = await handleResponse(response);
      console.log('✅ Perfil guardado exitosamente');
      return data;
    } catch (error) {
      console.error('Error guardando perfil:', error);
      throw error;
    }
  }

  // OBTENER PERFIL COMPLETO
  async getProfile({ signal } = {}) {
    try {
      const token = getToken();
      if (!token) {
        throw new Error('No hay sesión activa');
      }

      return await requestJson(`${API_URL}/profiles/get`, {
        method: 'GET',
        signal,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

    } catch (error) {
      console.error('Error obteniendo perfil:', error);
      throw error;
    }
  }
}

// Instancia singleton
export const auth = new AuthService();

// Funciones de utilidad
export const isAuthenticated = () => auth.isAuthenticated();
export const getCurrentUser = () => auth.getCurrentUser();
export const isDemoMode = () => auth.isDemoMode();
export const needsEmailVerification = () => auth.needsEmailVerification();
